import React, { useState } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import { Eye, EyeOff, Lock, CheckCircle2, AlertCircle, ArrowRight, Loader2, KeyRound } from "lucide-react";
import { useAuth } from "../context/AuthContext";

export default function ResetPassword() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");
  const navigate = useNavigate();
  const { resetPassword } = useAuth();

  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [isSuccess, setIsSuccess] = useState(false);
  const [isTokenExpired, setIsTokenExpired] = useState(false);

  // Password rules validation (matching NestJS backend requirements: min 8 chars, 1 uppercase, 1 lowercase, 1 number)
  const rules = {
    length: password.length >= 8,
    number: /\d/.test(password),
    uppercase: /[A-Z]/.test(password),
    lowercase: /[a-z]/.test(password),
  };

  const isPasswordValid = rules.length && rules.number && rules.uppercase && rules.lowercase;
  const doPasswordsMatch = password && password === confirmPassword;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    if (!token) {
      setError("Reset token is missing from URL.");
      return;
    }

    if (!isPasswordValid) {
      setError("Please satisfy all password complexity requirements (at least 8 chars, uppercase, lowercase, and number).");
      return;
    }

    if (!doPasswordsMatch) {
      setError("Passwords do not match.");
      return;
    }

    setLoading(true);

    try {
      await resetPassword(token, password);
      setIsSuccess(true);
    } catch (err) {
      const msg =
        err?.response?.data?.message ||
        err?.message ||
        "Failed to reset password. Please try again.";
      const formattedMsg = Array.isArray(msg) ? msg.join(", ") : msg;
      setError(formattedMsg);

      if (
        err?.response?.data?.isExpired ||
        formattedMsg.toLowerCase().includes("expired") ||
        formattedMsg.toLowerCase().includes("invalid")
      ) {
        setIsTokenExpired(true);
      }
    } finally {
      setLoading(false);
    }
  };

  if (!token || isTokenExpired) {
    return (
      <div
        style={{
          backgroundImage: "url(/images/login-bg.png)",
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
        className="min-h-screen w-screen flex items-center justify-center px-4 py-12 relative overflow-auto font-sans"
      >
        <div className="absolute inset-0 bg-black/40 backdrop-blur-sm pointer-events-none"></div>

        <div className="w-full max-w-[480px] relative z-10 bg-black/40 backdrop-blur-md rounded-3xl p-8 md:p-10 shadow-2xl border border-white/10 text-white text-center">
          <div className="w-16 h-16 bg-amber-500/20 border border-amber-400/30 rounded-full flex items-center justify-center mx-auto mb-4 text-amber-400">
            <AlertCircle className="w-9 h-9" />
          </div>
          <h2 className="text-2xl font-bold mb-2">Invalid or Expired Link</h2>
          <p className="text-gray-300 text-sm mb-6">
            {error || "This password reset link is invalid or has expired for security reasons."}
          </p>

          <Link
            to="/forgot-password"
            className="w-full bg-teal-500 hover:bg-teal-400 text-white font-semibold py-3.5 px-6 rounded-xl transition-all duration-300 flex items-center justify-center gap-2 shadow-lg shadow-teal-500/20 hover:scale-[1.02] no-underline"
          >
            <span>Request New Reset Link</span>
            <ArrowRight className="w-5 h-5" />
          </Link>

          <div className="mt-6">
            <Link to="/login" className="text-xs text-gray-400 hover:text-white transition-colors">
              Return to Login
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      style={{
        backgroundImage: "url(/images/login-bg.png)",
        backgroundSize: "cover",
        backgroundPosition: "center",
      }}
      className="min-h-screen w-screen flex items-center justify-center px-4 py-12 relative overflow-auto font-sans"
    >
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm pointer-events-none"></div>

      <div className="w-full max-w-[480px] relative z-10 bg-black/40 backdrop-blur-md rounded-3xl p-8 md:p-10 shadow-2xl border border-white/10 text-white">
        {/* Branding Header */}
        <div className="text-center mb-8">
          <Link to="/" className="inline-flex items-center gap-3 no-underline group mb-3">
            <img
              src="/images/sync_logo.png"
              alt="Sync & Explore"
              className="w-12 h-auto group-hover:scale-105 transition-transform duration-300"
            />
            <span className="text-2xl font-bold tracking-tight text-white">
              Sync & Explore
            </span>
          </Link>
          <p className="text-teal-300/80 text-sm font-medium">Reset Password</p>
        </div>

        {/* Success View */}
        {isSuccess ? (
          <div className="text-center py-4 animate-fadeIn">
            <div className="w-20 h-20 bg-teal-500/20 border border-teal-400/30 rounded-full flex items-center justify-center mx-auto mb-6 text-teal-400">
              <CheckCircle2 className="w-12 h-12" />
            </div>
            <h2 className="text-2xl font-bold text-white mb-2">Password Reset Complete!</h2>
            <p className="text-gray-300 text-sm mb-8">
              Your password has been successfully updated. You can now log in using your new credentials.
            </p>

            <button
              onClick={() => navigate("/login")}
              className="w-full bg-teal-500 hover:bg-teal-400 text-white font-semibold py-3.5 px-6 rounded-xl transition-all duration-300 flex items-center justify-center gap-2 shadow-lg shadow-teal-500/20 hover:scale-[1.02]"
            >
              <span>Back to Login</span>
              <ArrowRight className="w-5 h-5" />
            </button>
          </div>
        ) : (
          /* Form View */
          <form onSubmit={handleSubmit} className="space-y-5">
            <div className="text-center mb-6">
              <div className="w-14 h-14 bg-teal-500/20 border border-teal-400/30 rounded-full flex items-center justify-center mx-auto mb-3 text-teal-400">
                <KeyRound className="w-7 h-7" />
              </div>
              <h2 className="text-xl font-bold text-white mb-1">Create New Password</h2>
              <p className="text-gray-300 text-xs">Enter your new secure password below.</p>
            </div>

            {error && (
              <div className="bg-red-500/20 border border-red-500/30 text-red-300 text-xs p-3.5 rounded-xl flex items-center gap-2">
                <AlertCircle className="w-4 h-4 flex-shrink-0" />
                <span>{error}</span>
              </div>
            )}

            {/* New Password Input */}
            <div>
              <label className="block text-xs text-gray-300 font-medium mb-1.5">
                New Password
              </label>
              <div className="relative">
                <Lock className="absolute left-3.5 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  required
                  className="w-full bg-white/10 backdrop-blur-sm text-white placeholder-gray-400 rounded-xl py-3 pl-11 pr-11 text-sm focus:outline-none focus:ring-2 focus:ring-teal-400 border border-white/10"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-white transition-colors"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>

            {/* Confirm Password Input */}
            <div>
              <label className="block text-xs text-gray-300 font-medium mb-1.5">
                Confirm Password
              </label>
              <div className="relative">
                <Lock className="absolute left-3.5 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  type={showConfirmPassword ? "text" : "password"}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="••••••••"
                  required
                  className="w-full bg-white/10 backdrop-blur-sm text-white placeholder-gray-400 rounded-xl py-3 pl-11 pr-11 text-sm focus:outline-none focus:ring-2 focus:ring-teal-400 border border-white/10"
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  className="absolute right-3.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-white transition-colors"
                >
                  {showConfirmPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>

            {/* Realtime Password Rules */}
            <div className="bg-white/5 p-3.5 rounded-xl border border-white/5 space-y-1.5 text-xs">
              <div className={`flex items-center gap-2 ${rules.length ? "text-teal-400" : "text-gray-400"}`}>
                <div className={`w-1.5 h-1.5 rounded-full ${rules.length ? "bg-teal-400" : "bg-gray-500"}`}></div>
                <span>At least 8 characters</span>
              </div>
              <div className={`flex items-center gap-2 ${rules.uppercase ? "text-teal-400" : "text-gray-400"}`}>
                <div className={`w-1.5 h-1.5 rounded-full ${rules.uppercase ? "bg-teal-400" : "bg-gray-500"}`}></div>
                <span>At least one uppercase letter (A-Z)</span>
              </div>
              <div className={`flex items-center gap-2 ${rules.lowercase ? "text-teal-400" : "text-gray-400"}`}>
                <div className={`w-1.5 h-1.5 rounded-full ${rules.lowercase ? "bg-teal-400" : "bg-gray-500"}`}></div>
                <span>At least one lowercase letter (a-z)</span>
              </div>
              <div className={`flex items-center gap-2 ${rules.number ? "text-teal-400" : "text-gray-400"}`}>
                <div className={`w-1.5 h-1.5 rounded-full ${rules.number ? "bg-teal-400" : "bg-gray-500"}`}></div>
                <span>At least one number (0-9)</span>
              </div>
            </div>

            <button
              type="submit"
              disabled={loading || !isPasswordValid || !doPasswordsMatch}
              className="w-full bg-teal-500 hover:bg-teal-400 text-white font-semibold py-3.5 rounded-xl transition-all duration-300 flex items-center justify-center gap-2 shadow-lg shadow-teal-500/20 hover:scale-[1.02] disabled:opacity-50 disabled:hover:scale-100 mt-6"
            >
              {loading ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  <span>Resetting Password...</span>
                </>
              ) : (
                <span>Reset Password</span>
              )}
            </button>

            <div className="text-center pt-2">
              <Link to="/login" className="text-xs text-gray-400 hover:text-white transition-colors">
                Back to Login
              </Link>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
